import { useState } from 'preact/hooks';
import { motion, AnimatePresence } from 'framer-motion';
import { GOLD, ACCENT_GOLD, TEXT_BODY } from '../../lib/medievalTheme';
import { playUI, UI } from '../../utils/arena/uiSounds';
import { Tooltip } from '../ui/tooltip';
import { t } from '../../localization/t';

// Shared circular-button surface. Fully opaque. No alpha on fills or
// borders (the board must never show through), no backdrop-blur. The
// "lit" state reads as gold via a solid warm fill + gold border + glow
// shadow rather than a translucent tint. Solid border colours are the
// flattened-over-dark equivalents of the gold tints used elsewhere.
const REST_BG = 'rgb(18, 15, 11)';
const HOVER_BG = 'rgb(60, 48, 28)';
const ACTIVE_BG = 'rgb(74, 58, 26)';
const REST_BORDER = 'rgb(70, 55, 27)';

// Fan geometry, in cqw so it scales with the canvas like every other HUD
// element (1cqw = 16px at scale 1.0). The trigger is anchored on the
// right edge of the board, so the items radiate leftward. Angles use a
// screen convention: 0°=right, 90°=up, 180°=left, 270°=down. The arc is
// centred a little past straight-left (205°) so the whole fan sits low:
// the top item (Collection) clears the layer-switch pill above the
// trigger, and the bottom item (Tokens) lands between 6 and 7 o'clock.
const FAN_RADIUS_CQW = 5.1;
const FAN_ARC_DEG = 110;
const FAN_CENTER_DEG = 215;

function fanOffset(index, count) {
  // Single item sits at the arc centre; otherwise spread evenly across
  // the arc, smaller indices higher on screen.
  const angleDeg = count <= 1
    ? FAN_CENTER_DEG
    : FAN_CENTER_DEG - FAN_ARC_DEG / 2 + (FAN_ARC_DEG * index) / (count - 1);
  const rad = (angleDeg * Math.PI) / 180;
  // Screen Y grows downward, so negate the sine.
  return {
    dx: FAN_RADIUS_CQW * Math.cos(rad),
    dy: -FAN_RADIUS_CQW * Math.sin(rad),
  };
}

// Entry spring shared by every fanned item. A soft settle with a barely
// perceptible kiss of overshoot (same tuning family as StatusRingMenu).
const ITEM_SPRING = { type: 'spring', stiffness: 520, damping: 40, mass: 0.8 };

function circleButtonStyle(isActive) {
  return {
    background: isActive ? ACTIVE_BG : REST_BG,
    border: `1px solid ${isActive ? ACCENT_GOLD : REST_BORDER}`,
    color: isActive ? ACCENT_GOLD : TEXT_BODY,
    // Shadows may keep alpha. Only fills / borders must stay solid.
    boxShadow: isActive
      ? `0 0 16px ${GOLD} 0.45), inset 0 0 8px ${GOLD} 0.2)`
      : '0 8px 24px rgba(0,0,0,0.4), inset 0 0 4px rgba(0,0,0,0.25)',
  };
}

function hoverIn(e, isActive) {
  if (isActive) return;
  e.currentTarget.style.background = HOVER_BG;
  e.currentTarget.style.borderColor = ACCENT_GOLD;
}

function hoverOut(e, isActive) {
  if (isActive) return;
  e.currentTarget.style.background = REST_BG;
  e.currentTarget.style.borderColor = REST_BORDER;
}

// `•••` trigger glyph. The "more zones" affordance from the mockup.
function EllipsisIcon() {
  return (
    <svg style={{ width: '2.75cqw', height: '2.75cqw', pointerEvents: 'none' }} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="5" cy="12" r="2.2" />
      <circle cx="12" cy="12" r="2.2" />
      <circle cx="19" cy="12" r="2.2" />
    </svg>
  );
}

/**
 * Consolidated zones menu. A single circular trigger that fans out one
 * button per `item` when opened. Each item is `{ key, label, hint?,
 * isActive, onSelect, icon, accessory? }`:
 *   - `isActive` lights the item gold (its overlay is currently open)
 *   - `onSelect` opens / toggles that zone's overlay
 *   - `hint` is the optional hotkey string shown in the tooltip
 *   - `icon` is the SVG node rendered inside the circle
 *   - `accessory` is an optional node pinned to the item (e.g. the
 *     Collection visibility toggle), only shown while the fan is open
 *
 * The trigger lights gold when any item is active or the fan is open.
 */
export default function PileFanMenu({ items, triggerLabel = t('Zones') }) {
  const [open, setOpen] = useState(false);
  const anyActive = items.some((it) => it.isActive);

  const collapse = () => setOpen(false);
  const toggle = () => {
    playUI(open ? UI.CANCEL : UI.SELECT);
    setOpen((v) => !v);
  };

  return (
    <div className="relative pointer-events-auto" style={{ zIndex: open ? 1095 : 'auto' }}>
      {/* Transparent click-away. Closes the fan without dimming the
          board. Sits beneath the items but above the rest of the HUD. */}
      {open ? (
        <div
          className="fixed inset-0 z-[1090]"
          onClick={(e) => { e.stopPropagation(); collapse(); }}
          onContextMenu={(e) => { e.stopPropagation(); collapse(); }}
        />
      ) : null}

      <AnimatePresence>
        {open
          ? items.map((item, i) => {
              const { dx, dy } = fanOffset(i, items.length);
              return (
                <motion.div
                  key={item.key}
                  className="absolute inset-0 z-[1095] flex items-center justify-center pointer-events-none"
                  initial={{ x: '0cqw', y: '0cqw', scale: 0.4, opacity: 0 }}
                  animate={{ x: `${dx}cqw`, y: `${dy}cqw`, scale: 1, opacity: 1 }}
                  exit={{ x: '0cqw', y: '0cqw', scale: 0.4, opacity: 0 }}
                  transition={ITEM_SPRING}
                >
                  <div className="relative pointer-events-auto">
                    <Tooltip content={item.hint ? t('{label} ({hint})', { label: item.label, hint: item.hint }) : item.label} side="left">
                      <button
                        type="button"
                        aria-label={t('Open {name}', { name: item.label })}
                        className="relative size-16 rounded-full flex items-center justify-center cursor-pointer transition-all"
                        style={circleButtonStyle(item.isActive)}
                        onMouseEnter={(e) => hoverIn(e, item.isActive)}
                        onMouseLeave={(e) => hoverOut(e, item.isActive)}
                        onClick={(e) => {
                          e.stopPropagation();
                          item.onSelect();
                          collapse();
                        }}
                      >
                        {item.icon}
                      </button>
                    </Tooltip>
                    {item.accessory ?? null}
                  </div>
                </motion.div>
              );
            })
          : null}
      </AnimatePresence>

      <Tooltip content={triggerLabel} side="bottom">
        <button
          type="button"
          aria-label={triggerLabel}
          aria-expanded={open}
          className="relative size-16 rounded-full flex items-center justify-center cursor-pointer transition-all z-[1096]"
          style={circleButtonStyle(open || anyActive)}
          onMouseEnter={(e) => hoverIn(e, open || anyActive)}
          onMouseLeave={(e) => hoverOut(e, open || anyActive)}
          onClick={(e) => { e.stopPropagation(); toggle(); }}
        >
          <EllipsisIcon />
        </button>
      </Tooltip>
    </div>
  );
}
